"use client"
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';

const Footer = ({ articles }) => {
    const currentYear = new Date().getFullYear();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data.data.message);
                setEmail('');
            } else {
                setMessage(data.error || 'Prenumeration misslyckades');
            }
        } catch (error) {
            setMessage('Nätverksfel. Försök igen.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <footer className="bg-blue-50 text-blue-900 pt-16 pb-8">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">

                    {/* About Section */}
                    <div className="space-y-6">
                        <div className="flex items-center">
                            <Image
                                src="/uploads/logo-nobg-black.png"
                                alt="SecretJoy"
                                width={160}
                                height={50}
                                className="object-contain"
                            />
                        </div>
                        <p className="text-blue-700">
                            Upptäck spänningen i varje förpackning med våra unika hemliga presentboxar.
                        </p>
                        <div className="flex space-x-4">
                            {['facebook', 'instagram', 'tiktok'].map((social) => (
                                <Link
                                    key={social}
                                    href="#"
                                    className="text-blue-500 hover:text-yellow-400 transition"
                                    aria-label={social}
                                >
                                    <Image
                                        src={`/social/${social}.svg`}
                                        alt={social}
                                        width={24}
                                        height={24}
                                        className="hover:scale-110 transition-transform"
                                    />
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-blue-800">Snabbnavigation</h3>
                        <ul className="space-y-3">
                            <li><Link href="/om-oss" className="text-blue-600 hover:text-blue-900 transition hover:underline">Om oss</Link></li>
                            <li><Link href="/shop" className="text-blue-600 hover:text-blue-900 transition hover:underline">Våra paket</Link></li>
                            <li><Link href="/artiklar" className="text-blue-600 hover:text-blue-900 transition hover:underline">Artiklar</Link></li>
                            <li><Link href="/contact" className="text-blue-600 hover:text-blue-900 transition hover:underline">Kontakt</Link></li>
                        </ul>
                    </div>

                    {/* Blog Articles */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-blue-800">Senaste nytt</h3>
                        <ul className="space-y-3">
                            {articles && articles.slice(0, 4).map((article: any) => (
                                <li key={article._id}>
                                    <Link
                                        href={`/artiklar/${article.slug?.current}`}
                                        className="text-blue-600 hover:text-blue-900 transition hover:underline"
                                    >
                                        {article.title}
                                    </Link>
                                </li>
                            ))}
                            
                        </ul>
                    </div>

                    {/* Newsletter */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-semibold text-blue-800">Nyhetsbrev</h3>
                        <p className="text-blue-700">
                            Prenumerera för exklusiva erbjudanden och nya hemliga presentboxar!
                        </p>
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div className="flex">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Din e-post"
                                    className="px-4 py-3 rounded-l-md text-blue-900 w-full focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="bg-yellow-400 hover:bg-yellow-500 px-4 py-3 rounded-r-md transition-all disabled:opacity-50 text-blue-900 font-semibold"
                                >
                                    {isLoading ? 'Skickar...' : 'Prenumerera'}
                                </button>
                            </div>
                            {message && (
                                <p className={`text-sm ${message.includes('tack') ? 'text-green-500' : 'text-red-400'}`}>
                                    {message}
                                </p>
                            )}
                        </form>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-blue-200 pt-8 flex flex-col md:flex-row justify-between items-center">
                    <div className="flex items-center space-x-6 mb-4 md:mb-0">
                        <Link href="/privacy" className="text-blue-500 hover:text-blue-800 text-sm transition hover:underline">
                            Integritetspolicy
                        </Link>
                        <Link href="/terms" className="text-blue-500 hover:text-blue-800 text-sm transition hover:underline">
                            Användarvillkor
                        </Link>
                    </div>
                    <p className="text-blue-500 text-sm">
                        © {currentYear} SecretJoy. Alla rättigheter reserverade.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
